<template>
  <v-row>
    <v-col md="12">
      <div class="companies-search">
        <v-text-field
          v-model="searchKeyword"
          label="ค้นหาบริษัท (ชื่อ หรือ เลขประจำตัวผู้เสียภาษี)"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
        ></v-text-field>
      </div>

      <div
        v-for="section in sections"
        :key="section.key"
        class="companies-section"
      >
        <div class="companies-section-title">
          {{ section.title }}
          <span class="companies-section-count">({{ section.items.length }})</span>
        </div>
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr class="header-row">
                <th class="header-text header-text-left">ชื่อบริษัท</th>
                <th class="header-text header-text-left">เลขประจำตัวผู้เสียภาษี</th>
                <th class="header-text header-text-right">ดำเนินการ</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="company in section.items"
                :key="company.id"
                class="tw-cursor-pointer"
                :style="company.company.isInactive ? 'opacity: 0.5' : ''"
                @click="onClickCompanyDetail(company.companyID)"
              >
                <td>
                  <v-avatar
                    size="32"
                    color="#F5F5F5"
                    class="mr-3 font-24 logoText"
                    style="background-color: #fff"
                  >
                    <img
                      v-if="company.company.imageLogoUrl"
                      :src="company.company.imageLogoUrl + '=s' + logoResolution"
                      :alt="company.company.nameTH"
                      style="object-fit: contain"
                    />
                    <span
                      v-else-if="company.company.nameEN"
                      class="edit-text-avatar"
                    >{{ company.company.nameEN[0].toUpperCase() }}</span>
                    <span v-else />
                  </v-avatar>
                  {{ company.company.nameTH }}
                  <v-chip
                    v-if="environmentOf(company)"
                    x-small
                    :color="environmentOf(company).color"
                    text-color="white"
                    class="ml-2"
                  >{{ environmentOf(company).label }}</v-chip>
                  <v-chip
                    v-if="company.company.isInactive"
                    x-small
                    color="red"
                    text-color="white"
                    class="ml-2"
                  >สิ้นสุดสัญญา</v-chip>
                </td>
                <td>{{ company.company.taxID || "-" }}</td>
                <td class="text-right">
                  <v-btn
                    v-if="userCompany.isAdmin"
                    icon
                    color="black"
                    class="button-edit"
                    aria-label="แก้ไขข้อมูลบริษัท"
                    @click.stop="onClickOpenDialog(company.companyID)"
                  >
                    <img
                      src="/images/icons/edit_user.svg"
                      alt="edit-user"
                      style="vertical-align: middle; max-width: 26px"
                    />
                  </v-btn>
                </td>
              </tr>
              <tr v-if="!section.items.length && !loading">
                <td colspan="3" class="companies-empty">{{ section.empty }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>

      <dialog-edit-company></dialog-edit-company>
      <v-progress-linear indeterminate color="green" :active="loading"></v-progress-linear>
    </v-col>
  </v-row>
</template>

<script>
import {mapState} from "vuex";
import DialogEditCompany from "./dialog-edit-company"
import { getCompanyBadge } from "@/libs/companyEnvironments";

export default {
  name: 'companiesManagement',
  components:{
    DialogEditCompany
  },
  data() {
    return {
      searchKeyword: '',
    }
  },
  computed: {
    ...mapState({
      companies: state => state.companies.list,
      loading: state => state.companies.loading,
      isOpenModal: state => state.companies.isOpenModalCompany,
      userCompany: state => state.user.userCompany
    }),
    currentCompanyId() {
      return this.$route.params.companyId
    },
    // devicePixelRatio is stable for the session — resolve the logo size once
    // instead of recomputing it for every row on every render.
    logoResolution() {
      return Math.round(30 * window.devicePixelRatio)
    },
    // Drop the placeholder `{ company: {} }` the store seeds before getList resolves.
    validCompanies() {
      return this.companies.filter(c => c.companyID)
    },
    currentCompanies() {
      return this.validCompanies.filter(c => c.companyID === this.currentCompanyId)
    },
    // Case-insensitive keyword match against the company's Thai name,
    // English name, or tax ID. Falls back to the full list when the
    // search box is empty.
    filteredCompanies() {
      const keyword = (this.searchKeyword || '').trim().toLowerCase()
      if (!keyword) return this.validCompanies
      return this.validCompanies.filter(c => {
        const nameTH = (c.company?.nameTH || '').toLowerCase()
        const nameEN = (c.company?.nameEN || '').toLowerCase()
        const taxID = (c.company?.taxID || '').toLowerCase()
        return nameTH.includes(keyword) || nameEN.includes(keyword) || taxID.includes(keyword)
      })
    },
    sections() {
      const searching = !!(this.searchKeyword || '').trim()
      return [
        {
          key: 'current',
          title: 'รายชื่อบริษัทตอนนี้',
          items: this.currentCompanies,
          empty: 'ไม่พบบริษัทปัจจุบัน',
        },
        {
          key: 'all',
          title: 'รายชื่อบริษัทที่อยู่ทั้งหมด',
          items: this.filteredCompanies,
          empty: searching ? 'ไม่พบบริษัทที่ค้นหา' : 'ยังไม่มีบริษัท',
        },
      ]
    },
  },
  created() {
    this.$store.dispatch('companies/getList')
    let companyID = this.$route.params.companyId
    if (companyID) {
      this.$store.dispatch('user/getRoleUser', {companyId: companyID})
    }
  },
  methods:{
    onClickOpenDialog: function (companyID){
      if (this.isOpenModal) this.$store.dispatch('companies/setCompanyIDModal', {companyIDModal: ""})
      else this.$store.dispatch('companies/setCompanyIDModal', {companyIDModal: companyID})
      this.$store.dispatch('companies/setIsOpenModalCompany', {isOpenModal: !this.isOpenModal})
    },
    environmentOf: function (company){
      return getCompanyBadge(company.company)
    },
    onClickCompanyDetail: function (companyID){
      this.$router.push({
        name: 'Company.Settings.CompanyDetail',
        params: {
          companyId: this.$route.params.companyId,
          companyDetailId: companyID,
        },
      })
    },
  }
}
</script>
<style lang="scss">
.companies-search {
  max-width: 480px;
  margin-bottom: 20px;
}

.companies-section {
  margin-bottom: 28px;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.companies-section-title {
  font-size: 18px;
  font-weight: bold;
  color: #191919;
  margin-bottom: 10px;
}

.companies-section-count {
  font-size: 14px;
  font-weight: normal;
  color: #9e9e9e;
  margin-left: 4px;
}

.companies-empty {
  text-align: center !important;
  color: #9e9e9e;
  padding: 24px 0 !important;
}

.header-row {
  background-color: #f0f0f0 !important;
}

.header-text {
  font-size: 16px !important;
  font-weight: bold !important;
  text-align: center !important;
}

.header-text-left {
  text-align: left !important;
}

.header-text-right {
  text-align: right !important;
}

</style>
